// Movement, collision and line of sight. Integer world units (100 per tile). Only + - * /,
// sqrt and Math.round touch state, all of which are bit-identical across JS engines.
import { TILE } from '@wahala/maps';
export const TICK_MS = 50;
/** Base speed in world units per tick (about 4.4 tiles a second). */
export const BASE_SPEED = 22;
export const GHOST_SPEED_FACTOR = 1.25;
/** Keke collision radius. */
export const PLAYER_RADIUS = 30;
/** Longest single collision step. Half the radius, so no speed setting tunnels through a wall. */
export const MAX_SUBSTEP = PLAYER_RADIUS / 2;
/** Input axes are int8 on the wire. */
export const INPUT_MAX = 127;
const CELL = 2 * TILE;
const hashes = new WeakMap();
const key = (cx, cy) => `${cx},${cy}`;
/** Segments grouped by the grid cells their bounding box touches. Built once per map. */
function spatialHash(map) {
    let h = hashes.get(map);
    if (h)
        return h;
    h = { cells: new Map() };
    map.walls.forEach((s, i) => {
        const x0 = Math.floor(Math.min(s.a.x, s.b.x) / CELL);
        const x1 = Math.floor(Math.max(s.a.x, s.b.x) / CELL);
        const y0 = Math.floor(Math.min(s.a.y, s.b.y) / CELL);
        const y1 = Math.floor(Math.max(s.a.y, s.b.y) / CELL);
        for (let cx = x0; cx <= x1; cx++) {
            for (let cy = y0; cy <= y1; cy++) {
                const k = key(cx, cy);
                const list = h?.cells.get(k);
                if (list)
                    list.push(i);
                else
                    h?.cells.set(k, [i]);
            }
        }
    });
    hashes.set(map, h);
    return h;
}
/** Wall indices near a box, ascending and de-duplicated (order matters for determinism). */
function wallsNear(map, x0, y0, x1, y1) {
    const h = spatialHash(map);
    const out = new Set();
    for (let cx = Math.floor(x0 / CELL); cx <= Math.floor(x1 / CELL); cx++) {
        for (let cy = Math.floor(y0 / CELL); cy <= Math.floor(y1 / CELL); cy++) {
            for (const i of h.cells.get(key(cx, cy)) ?? [])
                out.add(i);
        }
    }
    return [...out].sort((a, b) => a - b);
}
function closestPoint(p, s) {
    const dx = s.b.x - s.a.x;
    const dy = s.b.y - s.a.y;
    const len2 = dx * dx + dy * dy;
    let u = len2 === 0 ? 0 : ((p.x - s.a.x) * dx + (p.y - s.a.y) * dy) / len2;
    if (u < 0)
        u = 0;
    else if (u > 1)
        u = 1;
    return { x: s.a.x + u * dx, y: s.a.y + u * dy };
}
/** Push a circle out of nearby walls; two passes settle corridor corners. */
function resolveWalls(map, pos) {
    let p = { x: pos.x, y: pos.y };
    for (let iter = 0; iter < 2; iter++) {
        const near = wallsNear(map, p.x - PLAYER_RADIUS, p.y - PLAYER_RADIUS, p.x + PLAYER_RADIUS, p.y + PLAYER_RADIUS);
        for (const i of near) {
            const s = map.walls[i];
            const c = closestPoint(p, s);
            const ox = p.x - c.x;
            const oy = p.y - c.y;
            const d2 = ox * ox + oy * oy;
            if (d2 >= PLAYER_RADIUS * PLAYER_RADIUS)
                continue;
            let nx;
            let ny;
            let d = Math.sqrt(d2);
            if (d === 0) {
                // exactly on the wall: push along the segment's normal
                const sx = s.b.x - s.a.x;
                const sy = s.b.y - s.a.y;
                const len = Math.sqrt(sx * sx + sy * sy) || 1;
                nx = -sy / len;
                ny = sx / len;
                d = 0;
            }
            else {
                nx = ox / d;
                ny = oy / d;
            }
            const push = PLAYER_RADIUS - d;
            p = { x: Math.round(p.x + nx * push), y: Math.round(p.y + ny * push) };
        }
    }
    return p;
}
function clampToMap(map, p) {
    return {
        x: Math.min(map.width - PLAYER_RADIUS, Math.max(PLAYER_RADIUS, p.x)),
        y: Math.min(map.height - PLAYER_RADIUS, Math.max(PLAYER_RADIUS, p.y)),
    };
}
/** True when an input vector is acceptable (magnitude at most 1, with a little quantisation slack). */
export function inputOk(dx, dy) {
    if (!Number.isInteger(dx) || !Number.isInteger(dy))
        return false;
    if (Math.abs(dx) > INPUT_MAX || Math.abs(dy) > INPUT_MAX)
        return false;
    return dx * dx + dy * dy <= INPUT_MAX * INPUT_MAX * 1.0201; // 1.01^2
}
/** Speed gained per tick under a pushed stick: full speed in about four ticks (0.2 s). */
export const accelPerTick = (speed) => Math.max(1, Math.round(speed / 4));
/** Speed shed per tick when the stick lets go or turns against the motion: stopped in about 0.1 s. */
export const brakePerTick = (speed) => Math.max(1, Math.round(speed / 2));
/** The velocity a keke wants for this input, reached at a limited rate (integer maths only). */
export function steer(vel, input, speedPerTick, brake = 1) {
    const { dx, dy } = input;
    const mag = Math.sqrt(dx * dx + dy * dy);
    const div = mag > INPUT_MAX ? mag : INPUT_MAX;
    const tx = Math.round((dx * speedPerTick) / div);
    const ty = Math.round((dy * speedPerTick) / div);
    const ex = tx - vel.x;
    const ey = ty - vel.y;
    if (ex === 0 && ey === 0)
        return vel;
    const e = Math.sqrt(ex * ex + ey * ey);
    const braking = (tx === 0 && ty === 0) || ex * vel.x + ey * vel.y < 0;
    // a wet road (brake under 1) sheds speed slower, so a stop takes longer
    const rate = braking ? Math.max(1, Math.round(brakePerTick(speedPerTick) * brake)) : accelPerTick(speedPerTick);
    if (e <= rate)
        return { x: tx, y: ty };
    return { x: vel.x + Math.round((ex * rate) / e), y: vel.y + Math.round((ey * rate) / e) };
}
/** What a wall leaves of one velocity axis: the part that actually moved, never more, never reversed. */
function kept(want, got) {
    if (want === 0 || Math.sign(want) !== Math.sign(got))
        return 0;
    return Math.abs(got) < Math.abs(want) ? got : want;
}
/**
 * Move one keke for one tick: steer the velocity toward the input, then move by it with wall
 * sliding. Shared by the server (authority) and the client (prediction), so it takes only what
 * both have: position and velocity, input, map, speed, whether it's a ghost and the weather's
 * braking (1 = dry; rain is under 1, so a stop takes longer).
 */
export function stepPlayer(k, input, map, speedPerTick, ghost, brake = 1) {
    const v = steer(k.vel, input, speedPerTick, ghost ? 1 : brake);
    if (v.x === 0 && v.y === 0)
        return { pos: k.pos, vel: v };
    if (ghost) {
        const pos = clampToMap(map, { x: k.pos.x + v.x, y: k.pos.y + v.y });
        return { pos, vel: { x: kept(v.x, pos.x - k.pos.x), y: kept(v.y, pos.y - k.pos.y) } };
    }
    // Fast settings move further than the radius in one tick; collide in short integer steps.
    const n = Math.max(1, Math.ceil(Math.max(Math.abs(v.x), Math.abs(v.y)) / MAX_SUBSTEP));
    let p = k.pos;
    for (let i = 1; i <= n; i++) {
        const sx = Math.round((v.x * i) / n) - Math.round((v.x * (i - 1)) / n);
        const sy = Math.round((v.y * i) / n) - Math.round((v.y * (i - 1)) / n);
        p = clampToMap(map, resolveWalls(map, { x: p.x + sx, y: p.y + sy }));
    }
    // a wall takes away the speed it stopped; sliding along it keeps the rest
    return { pos: p, vel: { x: kept(v.x, p.x - k.pos.x), y: kept(v.y, p.y - k.pos.y) } };
}
/**
 * Push a keke by (dx, dy) with walls in the way, in short integer steps like stepPlayer: a knock's
 * shove, two kekes pushed apart. Where it ends up is always a place a keke may be.
 */
export function slide(map, from, dx, dy) {
    const n = Math.max(1, Math.ceil(Math.max(Math.abs(dx), Math.abs(dy)) / MAX_SUBSTEP));
    let p = from;
    for (let i = 1; i <= n; i++) {
        const sx = Math.round((dx * i) / n) - Math.round((dx * (i - 1)) / n);
        const sy = Math.round((dy * i) / n) - Math.round((dy * (i - 1)) / n);
        p = clampToMap(map, resolveWalls(map, { x: p.x + sx, y: p.y + sy }));
    }
    return p;
}
export function dist2(a, b) {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return dx * dx + dy * dy;
}
const cross = (ax, ay, bx, by) => ax * by - ay * bx;
const sign = (n) => (n > 0 ? 1 : n < 0 ? -1 : 0);
/** Do segments p-q and a-b meet at all, touching included (exact on integers)? A sight line that
 *  grazes a wall end or runs along a wall is blocked, so corners where walls join never leak. */
function segmentsCross(p, q, s) {
    const rX = q.x - p.x;
    const rY = q.y - p.y;
    const sX = s.b.x - s.a.x;
    const sY = s.b.y - s.a.y;
    const d1 = cross(rX, rY, s.a.x - p.x, s.a.y - p.y);
    const d2 = cross(rX, rY, s.b.x - p.x, s.b.y - p.y);
    const d3 = cross(sX, sY, p.x - s.a.x, p.y - s.a.y);
    const d4 = cross(sX, sY, q.x - s.a.x, q.y - s.a.y);
    if (d1 === 0 && d2 === 0) {
        // collinear: blocked only where the two spans overlap
        return (Math.max(Math.min(p.x, q.x), Math.min(s.a.x, s.b.x)) <= Math.min(Math.max(p.x, q.x), Math.max(s.a.x, s.b.x)) &&
            Math.max(Math.min(p.y, q.y), Math.min(s.a.y, s.b.y)) <= Math.min(Math.max(p.y, q.y), Math.max(s.a.y, s.b.y)));
    }
    return sign(d1) * sign(d2) <= 0 && sign(d3) * sign(d4) <= 0;
}
/** Clear line of sight between two points (walls block; the map edge does not matter here). */
export function lineOfSight(map, a, b) {
    const near = wallsNear(map, Math.min(a.x, b.x), Math.min(a.y, b.y), Math.max(a.x, b.x), Math.max(a.y, b.y));
    for (const i of near) {
        if (segmentsCross(a, b, map.walls[i]))
            return false;
    }
    return true;
}
//# sourceMappingURL=geometry.js.map